import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, onSnapshot, Timestamp, arrayUnion } from "firebase/firestore";
import { db } from "@/services/firebase/init";
import { convertTimestampToDate } from "@/services/firebase/firestore";
import { FeedbackCase, Thread } from "@/types/feedback";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
  AdminErrorAlert,
} from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, Send, Loader2, UserPlus, XCircle } from "lucide-react";

export default function FeedbackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isStaff } = usePermissions();
  const [replyContent, setReplyContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackCase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // 使用 onSnapshot 即時監聽客服單更新
  useEffect(() => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    const docRef = doc(db, "feedbacks", id);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          setFeedback(null);
          setError(new Error("找不到此客服單"));
          setIsLoading(false);
          return;
        }

        const data = convertTimestampToDate(docSnap.data());
        const feedbackData: FeedbackCase = {
          ...data,
          caseId: docSnap.id,
        } as FeedbackCase;
        setFeedback(feedbackData);
        setIsLoading(false);
      },
      (err) => {
        console.error("Error getting feedback:", err);
        setError(err as Error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [id]);

  const handleUpdateStatus = async (status: FeedbackCase["status"]) => {
    if (!id || isUpdating) return;

    try {
      setIsUpdating(true);
      const docRef = doc(db, "feedbacks", id);
      const updateData: {
        status: FeedbackCase["status"];
        updatedAt: Timestamp;
        resolvedAt?: Timestamp;
        closedAt?: Timestamp;
      } = {
        status,
        updatedAt: Timestamp.now(),
      };

      if (status === "resolved") {
        updateData.resolvedAt = Timestamp.now();
      } else if (status === "closed") {
        updateData.closedAt = Timestamp.now();
      }

      await updateDoc(docRef, updateData);
    } catch (err) {
      console.error("Error updating feedback status:", err);
      alert("更新狀態失敗：" + (err instanceof Error ? err.message : "未知錯誤"));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdatePriority = async (priority: FeedbackCase["priority"]) => {
    if (!id || isUpdating) return;

    try {
      setIsUpdating(true);
      const docRef = doc(db, "feedbacks", id);
      await updateDoc(docRef, {
        priority,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error("Error updating feedback priority:", err);
      alert("更新優先級失敗：" + (err instanceof Error ? err.message : "未知錯誤"));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAssign = async () => {
    if (!id || !user?.uid || isUpdating) return;

    try {
      setIsUpdating(true);
      const docRef = doc(db, "feedbacks", id);
      await updateDoc(docRef, {
        assignedTo: user.uid,
        status: "in_progress",
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error("Error assigning feedback:", err);
      alert("指派失敗：" + (err instanceof Error ? err.message : "未知錯誤"));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = async () => {
    if (!id || isUpdating) return;

    try {
      setIsUpdating(true);
      const docRef = doc(db, "feedbacks", id);
      await updateDoc(docRef, {
        status: "closed",
        closedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error("Error closing feedback:", err);
      alert("關閉客服單失敗：" + (err instanceof Error ? err.message : "未知錯誤"));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() || isSubmitting || !id || !user?.uid) return;

    const trimmedContent = replyContent.trim();
    if (!trimmedContent) {
      alert("回覆內容不能為空");
      return;
    }
    if (trimmedContent.length > 1000) {
      alert("回覆內容長度不能超過 1000 字元");
      return;
    }

    try {
      setIsSubmitting(true);

      if (!feedback) {
        throw new Error("客服單數據未載入");
      }

      const docRef = doc(db, "feedbacks", id);
      const threads = feedback.threads || [];

      const threadId = `${Date.now()}_${user.uid.substring(0, 8)}_${Math.random().toString(36).substring(2, 9)}`;

      const newThread: Thread = {
        id: threadId,
        authorId: user.uid,
        authorName: user.displayName || "客服",
        authorType: "staff",
        content: trimmedContent,
        createdAt: new Date(),
        isRead: false,
      };

      const threadForFirestore = {
        ...newThread,
        createdAt: Timestamp.now(),
      };

      await updateDoc(docRef, {
        threads: arrayUnion(threadForFirestore),
        updatedAt: Timestamp.now(),
      });

      setReplyContent("");
    } catch (err) {
      console.error("Error adding thread:", err);
      alert("發送回覆失敗：" + (err instanceof Error ? err.message : "未知錯誤"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusLabel = (status: FeedbackCase["status"]): string => {
    switch (status) {
      case "pending": return "待處理";
      case "in_progress": return "處理中";
      case "resolved": return "處理完";
      case "closed": return "已關閉";
      default: return status;
    }
  };

  const getTypeLabel = (type: FeedbackCase["type"]): string => {
    switch (type) {
      case "transaction": return "交易相關";
      case "video": return "影片相關";
      case "account": return "帳戶相關";
      case "suggestion": return "建議";
      case "other": return "其他";
      default: return type;
    }
  };

  const getPriorityLabel = (priority: FeedbackCase["priority"]): string => {
    switch (priority) {
      case "urgent": return "緊急";
      case "normal": return "一般";
      case "low": return "低";
      default: return priority;
    }
  };

  if (!isStaff) {
    return (
      <AdminPage>
        <AdminErrorAlert message="您沒有權限訪問此頁面。" />
      </AdminPage>
    );
  }

  if (isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  if (error || !feedback) {
    return (
      <AdminPage>
        <AdminErrorAlert
          message={error instanceof Error ? error.message : "找不到此客服單"}
        />
        <Button variant="outline" onClick={() => navigate("/feedbacks")} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          返回列表
        </Button>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title="客服單詳情"
        subtitle={feedback.caseId}
        actions={
          <Button variant="outline" onClick={() => navigate("/feedbacks")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回列表
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左側：基本資訊和操作 */}
        <div className="lg:col-span-1 space-y-6">
          {/* 基本資訊 */}
          <Card>
            <CardHeader>
              <CardTitle>基本資訊</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">狀態</Label>
                <Select
                  value={feedback.status}
                  onValueChange={(val) => handleUpdateStatus(val as FeedbackCase["status"])}
                  disabled={isUpdating}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">待處理</SelectItem>
                    <SelectItem value="in_progress">處理中</SelectItem>
                    <SelectItem value="resolved">處理完</SelectItem>
                    <SelectItem value="closed">已關閉</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">優先級</Label>
                <Select
                  value={feedback.priority}
                  onValueChange={(val) => handleUpdatePriority(val as FeedbackCase["priority"])}
                  disabled={isUpdating}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">緊急</SelectItem>
                    <SelectItem value="normal">一般</SelectItem>
                    <SelectItem value="low">低</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-muted-foreground">類型</Label>
                <p className="text-foreground mt-1">
                  {getTypeLabel(feedback.type)}
                </p>
              </div>

              <div>
                <Label className="text-muted-foreground">建立時間</Label>
                <p className="text-foreground mt-1">
                  {feedback.createdAt
                    ? feedback.createdAt.toLocaleString("zh-TW")
                    : "-"}
                </p>
              </div>

              {feedback.resolvedAt && (
                <div>
                  <Label className="text-muted-foreground">解決時間</Label>
                  <p className="text-foreground mt-1">
                    {feedback.resolvedAt.toLocaleString("zh-TW")}
                  </p>
                </div>
              )}

              {feedback.rating && (
                <div>
                  <Label className="text-muted-foreground">評分</Label>
                  <div className="mt-1 flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span
                        key={i}
                        className={`text-xl ${
                          i < feedback.rating!
                            ? "text-yellow-400"
                            : "text-gray-300"
                        }`}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  {feedback.ratingComment && (
                    <p className="text-foreground mt-1 text-sm">
                      {feedback.ratingComment}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 用戶資訊 */}
          <Card>
            <CardHeader>
              <CardTitle>用戶資訊</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-muted-foreground">用戶 UID</Label>
                <p className="text-foreground mt-1 font-mono text-sm break-all">
                  {feedback.userId}
                </p>
              </div>
              {feedback.displayName && (
                <div>
                  <Label className="text-muted-foreground">稱謂</Label>
                  <p className="text-foreground mt-1">
                    {feedback.displayName}
                  </p>
                </div>
              )}
              {feedback.email && (
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="text-foreground mt-1">
                    {feedback.email}
                  </p>
                </div>
              )}
              {feedback.isAnonymous && (
                <div>
                  <Badge variant="outline">匿名用戶</Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 操作 */}
          {feedback.status !== "closed" && (
            <Card>
              <CardHeader>
                <CardTitle>操作</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  className="w-full"
                  onClick={handleAssign}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="mr-2 h-4 w-4" />
                  )}
                  {isUpdating ? "處理中..." : "指派給我"}
                </Button>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={handleClose}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="mr-2 h-4 w-4" />
                  )}
                  {isUpdating ? "處理中..." : "關閉客服單"}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* 右側：標題、描述和對話串 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 標題和描述 */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {feedback.title}
              </h2>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {feedback.description}
              </p>
            </CardContent>
          </Card>

          {/* 對話串 */}
          <Card>
            <CardHeader>
              <CardTitle>對話記錄 ({feedback.threads.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {feedback.threads.map((thread) => (
                  <div
                    key={thread.id}
                    className={`p-4 rounded-lg ${
                      thread.authorType === "staff"
                        ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
                        : "bg-muted/50 border"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="font-semibold text-foreground">
                          {thread.authorName}
                        </span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {thread.authorType === "staff" ? "客服" : "用戶"}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {thread.createdAt
                          ? thread.createdAt.toLocaleString("zh-TW")
                          : "-"}
                      </span>
                    </div>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {thread.content}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 回覆框 */}
          {feedback.status !== "closed" && (
            <Card>
              <CardHeader>
                <CardTitle>回覆</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="輸入回覆內容..."
                  rows={4}
                  className="resize-none"
                />
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={handleReply}
                    disabled={!replyContent.trim() || isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    {isSubmitting ? "發送中..." : "發送"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminPage>
  );
}
